'use client';

import { useEffect, useState } from 'react';

import { AppConfig, FeaturesConfig, getConfigManager } from './manager';

/**
 * Configuration Hooks
 * React hooks for accessing and managing configuration
 */

/**
 * Hook to access configuration
 */
export function useConfiguration() {
  const [config, setConfig] = useState<AppConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const manager = getConfigManager();

    try {
      const currentConfig = manager.getConfig();
      setConfig(currentConfig);
      setLoading(false);
    } catch (err) {
      setError(err as Error);
      setLoading(false);
    }

    // Subscribe to config changes
    const unsubscribe = manager.subscribe((newConfig) => {
      setConfig(newConfig);
    });

    return unsubscribe;
  }, []);

  return { config, loading, error };
}

/**
 * Hook to check if a feature is enabled
 */
export function useFeature(feature: keyof FeaturesConfig): boolean {
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    const manager = getConfigManager();
    setEnabled(manager.isFeatureEnabled(feature));

    const unsubscribe = manager.subscribe(() => {
      setEnabled(manager.isFeatureEnabled(feature));
    });

    return unsubscribe;
  }, [feature]);

  return enabled;
}

/**
 * Hook to access metadata
 */
export function useMetadata<T = any>(key: string, defaultValue?: T): T | undefined {
  const [value, setValue] = useState<T | undefined>(defaultValue);

  useEffect(() => {
    const manager = getConfigManager();
    setValue(manager.getMetadata<T>(key, defaultValue));

    const unsubscribe = manager.subscribe(() => {
      setValue(manager.getMetadata<T>(key, defaultValue));
    });

    return unsubscribe;
  }, [key, defaultValue]);

  return value;
}

/**
 * Hook to get a configuration section
 */
export function useConfigSection<K extends keyof AppConfig>(section: K): AppConfig[K] | null {
  const [sectionData, setSectionData] = useState<AppConfig[K] | null>(null);

  useEffect(() => {
    const manager = getConfigManager();

    try {
      setSectionData(manager.getSection(section));
    } catch (_) {
      setSectionData(null);
    }

    const unsubscribe = manager.subscribe((config) => {
      setSectionData(config[section]);
    });

    return unsubscribe;
  }, [section]);

  return sectionData;
}
